import { useId, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import HtmlText from '../components/HtmlText';
import { FeedbackSurveyCard } from './FeedbackSurveyShell';

/** Display only acknowledged response/payment data supplied by the canonical session. */
export function FeedbackAcknowledgedSummary({
  kind,
  badge,
  title,
  body,
  radius,
  editLabel,
  onEdit,
  disabled,
}: {
  kind: 'rating' | 'tip';
  badge: ReactNode;
  title: string;
  body?: string;
  radius: number;
  editLabel?: string;
  onEdit?: () => void;
  disabled?: boolean;
}) {
  const titleId = useId();
  return (
    <Box role="group" aria-labelledby={titleId} data-feedback-summary={kind}>
      <FeedbackSurveyCard radius={radius}>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'flex-start' }}>
          <Box
            data-feedback-summary-badge={kind}
            sx={{
              minWidth: 48,
              height: 48,
              flexShrink: 0,
              px: 1,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '6px',
              bgcolor: 'var(--cx-brand-button-selected-bg)',
              color: 'var(--cx-brand-button-selected-text)',
              fontWeight: 600,
              fontSize: 17,
              lineHeight: '26px',
              letterSpacing: 0,
            }}
          >
            {badge}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.25,
                alignItems: 'flex-start',
              }}
            >
              <HtmlText
                id={titleId}
                component="div"
                html={title}
                variant="subtitle1"
                sx={{
                  flex: '1 1 120px',
                  minWidth: 0,
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: '24px',
                  letterSpacing: 0,
                }}
              />
              {onEdit && editLabel && (
                <Button
                  type="button"
                  disabled={disabled}
                  onClick={onEdit}
                  sx={{
                    minHeight: 24,
                    minWidth: 24,
                    maxWidth: '100%',
                    flexShrink: 0,
                    p: 0,
                    textTransform: 'none',
                    textDecoration: 'underline',
                    overflowWrap: 'anywhere',
                    color: 'var(--cx-brand-text)',
                    fontWeight: 600,
                    fontSize: 14,
                    lineHeight: '22px',
                    letterSpacing: 0,
                  }}
                >
                  {editLabel}
                </Button>
              )}
            </Box>
            {body && (
              <HtmlText
                component="div"
                html={body}
                variant="body2"
                sx={{
                  mt: 0.25,
                  fontSize: 14,
                  lineHeight: '22px',
                  letterSpacing: 0,
                }}
              />
            )}
          </Box>
        </Stack>
      </FeedbackSurveyCard>
    </Box>
  );
}
